<template>
  <div class="anomalies-container">
    <!-- Описание формы -->
    <div class="description-block">
      <q-icon name="history" color="primary" size="24px" />
      <div class="description-text">
        <strong>Журнал изменений сметы</strong> — история всех изменений при пересчёте сметы с учётом АВР.
        <br/>
        <span class="description-subtitle">
          Когда работа или материал уже сданы в АВР (Акт выполненных работ), их нельзя изменить напрямую.
          Все расхождения фиксируются здесь для контроля.
        </span>
      </div>
    </div>
    
    <q-expansion-item
      class="cases-expansion"
      icon="help_outline"
      label="Подробное описание всех кейсов"
      header-class="cases-header"
      dense
    >
      <div class="cases-content">
        <div class="case-group">
          <div class="case-title positive">✓ Положительные изменения (применяются автоматически)</div>
          <div class="case-item">
            <q-badge color="green" label="Объём работы увеличился" />
            <span>Объём работы в пакете стал больше, чем было в АВР. Разница автоматически добавляется в группу доп. работ (ADD_WORK_1/2/3). В комментарии указано, в какую именно группу.</span>
          </div>
          <div class="case-item">
            <q-badge color="blue" label="Перенос в доп. работы" />
            <span>Работа была в группе (например, "Стены"), но эта группа уже проверена/сдана. Работа автоматически переносится в свободную группу ADD_WORK. Это происходит, когда добавляют новые работы после сдачи группы.</span>
          </div>
        </div>
        
        <div class="case-group">
          <div class="case-title warning">⚠ Расхождения (только логируются, смета НЕ меняется)</div>
          <div class="case-item">
            <q-badge color="orange" label="Объём работы уменьшился" />
            <span>Объём работы в пакете стал меньше, чем сдано в АВР. Уменьшить уже сданный объём нельзя — это зафиксировано в официальном акте. Требуется ручная проверка.</span>
          </div>
          <div class="case-item">
            <q-badge color="orange" label="Объём материала уменьшился" />
            <span>Количество материала стало меньше, чем было в АВР. Аналогично работам — уменьшить нельзя.</span>
          </div>
          <div class="case-item">
            <q-badge color="red" label="Работа удалена" />
            <span>Работа полностью исчезла из сметы после пересчёта (объём = 0), но она уже была в АВР. Удалить из сметы нельзя — акт подписан.</span>
          </div>
          <div class="case-item">
            <q-badge color="red" label="Материал удалён" />
            <span>Материал полностью убрали из работы, но он уже был в АВР. Может произойти при изменении пакета клиента.</span>
          </div>
        </div>
        
        <div class="case-group">
          <div class="case-title anomaly">🔍 Аномалии (требуют внимания)</div>
          <div class="case-item">
            <q-badge color="purple" label="Новый материал (аномалия)" />
            <span>Появился новый материал, которого не было в АВР, но объём самой работы НЕ увеличился. Это странно: откуда материал, если работа та же? Возможно, изменился состав работы в пакете.</span>
          </div>
          <div class="case-item">
            <q-badge color="purple" label="Материал увеличился (аномалия)" />
            <span>Количество материала выросло, но объём работы не увеличился. Материалы обычно привязаны к объёму — увеличение без роста работы подозрительно.</span>
          </div>
        </div>
      </div>
    </q-expansion-item>

    <!-- Фильтры -->
    <div class="filters-section">
      <div class="filters-row">
        <q-input
          v-model="searchQuery"
          label="Быстрый поиск"
          dense
          outlined
          style="width: 200px;"
          hide-bottom-space
          clearable
          debounce="300"
        >
          <template v-slot:prepend>
            <q-icon name="search" />
          </template>
        </q-input>
        <q-select
          v-model="filterDateTime"
          :options="dateTimeOptions"
          label="Дата и время"
          dense
          outlined
          emit-value
          map-options
          style="width: 180px;"
          hide-bottom-space
          clearable
        />
        <q-select
          v-model="filterEntrance"
          :options="entranceOptions"
          label="Подъезд"
          dense
          outlined
          emit-value
          map-options
          style="width: 110px;"
          hide-bottom-space
          clearable
        />
        <q-select
          v-model="filterFloor"
          :options="floorOptions"
          label="Этаж"
          dense
          outlined
          emit-value
          map-options
          style="width: 100px;"
          hide-bottom-space
          clearable
        />
        <q-select
          v-model="filterType"
          :options="typeOptions"
          label="Тип"
          dense
          outlined
          emit-value
          map-options
          style="width: 200px;"
          hide-bottom-space
          clearable
        />
        <q-select
          v-model="filterWork"
          :options="workOptions"
          label="Работа"
          dense
          outlined
          emit-value
          map-options
          style="width: 200px;"
          hide-bottom-space
          clearable
          use-input
          input-debounce="200"
          @filter="filterWorkFn"
        />
        <q-select
          v-model="filterMaterial"
          :options="materialOptions"
          label="Материал"
          dense
          outlined
          emit-value
          map-options
          style="width: 200px;"
          hide-bottom-space
          clearable
          use-input
          input-debounce="200"
          @filter="filterMaterialFn"
        />
      </div>
      <div class="filters-row-second">
        <div class="summary" v-if="filteredData?.length">
          <q-chip color="orange" text-color="white" icon="list" size="sm">
            {{ filteredData.length }} записей
          </q-chip>
          <q-chip color="red-4" text-color="white" v-if="totalDiffSum !== 0" size="sm">
            Разница: <Amount :value="totalDiffSum" :round="2" /> тг
          </q-chip>
        </div>
        <q-btn 
          v-if="hasActiveFilters" 
          flat 
          dense 
          color="grey" 
          icon="clear_all" 
          label="Сбросить фильтры"
          @click="resetFilters"
        />
      </div>
    </div>

    <q-linear-progress v-if="isFetching" indeterminate />

    <div v-if="!isFetching && !data?.length" class="empty-state">
      <q-icon name="check_circle" size="48px" color="green" />
      <div class="empty-text">Аномалий не найдено</div>
      <div class="empty-subtext">Все изменения сметы были успешно применены</div>
    </div>

    <div v-else-if="!isFetching && data?.length && !filteredData?.length" class="empty-state">
      <q-icon name="search_off" size="48px" color="grey" />
      <div class="empty-text">Ничего не найдено</div>
      <div class="empty-subtext">Попробуйте изменить параметры поиска</div>
    </div>

    <CustomTable
      v-if="filteredData?.length"
      title="Журнал изменений сметы"
      :rowsData="tableRows"
      :customHeader="true"
      :custom-body="true"
      :small="true"
    >
      <template #customHeader>
        <q-tr>
          <q-th align="center" style="width: 50px;">#</q-th>
          <q-th align="center" style="width: 160px;">Дата и время</q-th>
          <q-th align="center" style="width: 80px;">Подъезд</q-th>
          <q-th align="center" style="width: 60px;">Этаж</q-th>
          <q-th align="left">Тип</q-th>
          <q-th align="left">Работа</q-th>
          <q-th align="left">Материал</q-th>
          <q-th align="right" style="width: 90px;">Было</q-th>
          <q-th align="right" style="width: 90px;">Стало</q-th>
          <q-th align="right" style="width: 100px;">Разница</q-th>
          <q-th align="right" style="width: 110px;">Сумма разн.</q-th>
        </q-tr>
      </template>
      <template #customBody="props">
        <q-tr :class="getRowClass(props.row)">
          <custom-table-cell align="center">
            {{ props.row.index }}
          </custom-table-cell>
          <custom-table-cell align="center">
            {{ props.row.created_at_str }}
          </custom-table-cell>
          <custom-table-cell align="center">
            {{ props.row.entrance || '-' }}
          </custom-table-cell>
          <custom-table-cell align="center">
            {{ props.row.floor || '-' }}
          </custom-table-cell>
          <custom-table-cell align="left">
            <q-badge
              :color="getDiffTypeColor(props.row.diff_type)"
              :label="getDiffTypeName(props.row.diff_type)"
              class="diff-badge"
            />
            <q-tooltip>
              {{ getDiffTypeDescription(props.row.diff_type) }}
            </q-tooltip>
          </custom-table-cell>
          <custom-table-cell align="left">
            <span class="work-name">{{ props.row.work_set_name }}</span>
          </custom-table-cell>
          <custom-table-cell align="left">
            {{ props.row.material_name || '-' }}
          </custom-table-cell>
          <custom-table-cell align="right">
            {{ formatNumber(props.row.avr_amount) }}
          </custom-table-cell>
          <custom-table-cell align="right">
            {{ formatNumber(props.row.new_amount) }}
          </custom-table-cell>
          <custom-table-cell align="right" :class="getDiffClass(props.row.diff_amount)">
            {{ formatDiff(props.row.diff_amount) }}
          </custom-table-cell>
          <custom-table-cell align="right" :class="getDiffClass(props.row.diff_sum)">
            <Amount :value="props.row.diff_sum" :round="2" />
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

import Amount from '@/components/common/Amount.vue';
import CustomTable from '@/components/common/CustomTable';
import CustomTableCell from '@/components/common/CustomTable/custom-table-cell';
import { getSmetaAnomalies } from './services';

// Справочник типов изменений с подробными описаниями
const DIFF_TYPE_LABELS = {
  WORK_INCREASE: {
    name: 'Объём работы увеличился',
    description: 'Объём работы вырос. Разница добавлена в группу дополнительных работ (ADD_WORK)',
  },
  WORK_MOVED_TO_ADD_WORK: {
    name: 'Перенос в доп. работы',
    description: 'Работа перенесена в группу ADD_WORK, так как её предыдущая группа уже была проверена',
  },
  WORK_DELETED: {
    name: 'Работа удалена',
    description: 'Работа из АВР полностью исчезла после пересчёта. Новый объём = 0, но работа уже была принята в АВР — удалить нельзя',
  },
  WORK_DECREASE: {
    name: 'Объём работы уменьшился',
    description: 'Объём работы в пакете стал меньше, чем было в АВР. Уменьшить уже принятый объём нельзя',
  },
  WORK_ZEROED: {
    name: 'Работа обнулена',
    description: 'Объём работы стал равен нулю',
  },
  MATERIAL_DELETED: {
    name: 'Материал удалён',
    description: 'Материал полностью исчез из сметы после пересчёта. Был в АВР, теперь новый объём = 0. Удалить из сметы нельзя, так как АВР уже подписан',
  },
  MATERIAL_DECREASE: {
    name: 'Объём материала уменьшился',
    description: 'Количество материала стало меньше, чем было в АВР. Уменьшить уже принятый материал нельзя',
  },
  MATERIAL_NEW_ANOMALY: {
    name: 'Новый материал (аномалия)',
    description: 'Появился новый материал (которого не было в АВР), но объём работы не увеличился. Аномалия: откуда взялся материал, если работа та же?',
  },
  MATERIAL_INCREASE_ANOMALY: {
    name: 'Материал увеличился (аномалия)',
    description: 'Количество материала выросло, но объём работы не увеличился. Аномалия: материал привязан к объёму, увеличение без роста работы подозрительно',
  },
};

const props = defineProps({
  projectId: {
    type: Number,
    required: true,
  },
});

const isFetching = ref(false);
const data = ref([]);
const searchQuery = ref('');

// Фильтры
const filterDateTime = ref(null);
const filterEntrance = ref(null);
const filterFloor = ref(null);
const filterType = ref(null);
const filterWork = ref(null);
const filterMaterial = ref(null);

// Опции для фильтров (для работы и материала - с возможностью поиска)
const workOptionsFiltered = ref([]);
const materialOptionsFiltered = ref([]);

// Уникальные подъезды
const entranceOptions = computed(() => {
  const set = new Set();
  data.value.forEach((row) => {
    if (row.entrance) set.add(row.entrance);
  });
  return Array.from(set)
    .sort((a, b) => a - b)
    .map((v) => ({ label: String(v), value: v }));
});

// Уникальные этажи
const floorOptions = computed(() => {
  const set = new Set();
  data.value.forEach((row) => {
    if (row.floor !== null && row.floor !== undefined) set.add(row.floor);
  });
  return Array.from(set)
    .sort((a, b) => a - b)
    .map((v) => ({ label: String(v), value: v }));
});

// Уникальные типы
const typeOptions = computed(() => {
  const set = new Set();
  data.value.forEach((row) => {
    if (row.diff_type) set.add(row.diff_type);
  });
  return Array.from(set).map((v) => ({
    label: getDiffTypeName(v),
    value: v,
  }));
});

// Уникальные даты/время
const dateTimeOptions = computed(() => {
  const set = new Set();
  data.value.forEach((row) => {
    if (row.created_at_str) set.add(row.created_at_str);
  });
  return Array.from(set)
    .sort()
    .reverse()
    .map((v) => ({ label: v, value: v }));
});

// Все уникальные работы
const allWorkOptions = computed(() => {
  const set = new Set();
  data.value.forEach((row) => {
    if (row.work_set_name) set.add(row.work_set_name);
  });
  return Array.from(set)
    .sort()
    .map((v) => ({ label: v, value: v }));
});

// Все уникальные материалы
const allMaterialOptions = computed(() => {
  const set = new Set();
  data.value.forEach((row) => {
    if (row.material_name) set.add(row.material_name);
  });
  return Array.from(set)
    .sort()
    .map((v) => ({ label: v, value: v }));
});

// Фильтруемые опции для работ
const workOptions = computed(() => workOptionsFiltered.value);
const filterWorkFn = (val, update) => {
  update(() => {
    if (!val) {
      workOptionsFiltered.value = allWorkOptions.value;
    } else {
      const needle = val.toLowerCase();
      workOptionsFiltered.value = allWorkOptions.value.filter(
        (opt) => opt.label.toLowerCase().includes(needle),
      );
    }
  });
};

// Фильтруемые опции для материалов
const materialOptions = computed(() => materialOptionsFiltered.value);
const filterMaterialFn = (val, update) => {
  update(() => {
    if (!val) {
      materialOptionsFiltered.value = allMaterialOptions.value;
    } else {
      const needle = val.toLowerCase();
      materialOptionsFiltered.value = allMaterialOptions.value.filter(
        (opt) => opt.label.toLowerCase().includes(needle),
      );
    }
  });
};

// Есть ли активные фильтры
const hasActiveFilters = computed(() => {
  return (
    searchQuery.value ||
    filterDateTime.value !== null ||
    filterEntrance.value !== null ||
    filterFloor.value !== null ||
    filterType.value !== null ||
    filterWork.value !== null ||
    filterMaterial.value !== null
  );
});

// Сброс всех фильтров
const resetFilters = () => {
  searchQuery.value = '';
  filterDateTime.value = null;
  filterEntrance.value = null;
  filterFloor.value = null;
  filterType.value = null;
  filterWork.value = null;
  filterMaterial.value = null;
};

const getDiffTypeName = (diffType) => {
  return DIFF_TYPE_LABELS[diffType]?.name || diffType;
};

const getDiffTypeDescription = (diffType) => {
  return DIFF_TYPE_LABELS[diffType]?.description || '';
};

// Фильтрация по всем критериям
const filteredData = computed(() => {
  let result = data.value;

  // Фильтр по дате/времени
  if (filterDateTime.value !== null) {
    result = result.filter((row) => row.created_at_str === filterDateTime.value);
  }

  // Фильтр по подъезду
  if (filterEntrance.value !== null) {
    result = result.filter((row) => row.entrance === filterEntrance.value);
  }

  // Фильтр по этажу
  if (filterFloor.value !== null) {
    result = result.filter((row) => row.floor === filterFloor.value);
  }

  // Фильтр по типу
  if (filterType.value !== null) {
    result = result.filter((row) => row.diff_type === filterType.value);
  }

  // Фильтр по работе
  if (filterWork.value !== null) {
    result = result.filter((row) => row.work_set_name === filterWork.value);
  }

  // Фильтр по материалу
  if (filterMaterial.value !== null) {
    result = result.filter((row) => row.material_name === filterMaterial.value);
  }

  // Поиск по тексту
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase().trim();
    result = result.filter((row) => {
      const diffTypeName = getDiffTypeName(row.diff_type)?.toLowerCase() || '';
      const workSetName = row.work_set_name?.toLowerCase() || '';
      const materialName = row.material_name?.toLowerCase() || '';
      const reasonName = row.reason_name?.toLowerCase() || '';
      const comments = row.comments?.toLowerCase() || '';
      const entrance = String(row.entrance || '');
      const floor = String(row.floor || '');

      return (
        diffTypeName.includes(query) ||
        workSetName.includes(query) ||
        materialName.includes(query) ||
        reasonName.includes(query) ||
        comments.includes(query) ||
        entrance.includes(query) ||
        floor.includes(query)
      );
    });
  }

  return result;
});

const totalDiffSum = computed(() =>
  filteredData.value.reduce((acc, item) => acc + (item.diff_sum || 0), 0),
);

// Данные с нумерацией
const tableRows = computed(() => {
  return filteredData.value.map((row, idx) => ({
    ...row,
    index: idx + 1,
  }));
});

const loadData = async () => {
  if (!props.projectId) {
    data.value = [];
    return;
  }

  isFetching.value = true;
  const res = await getSmetaAnomalies(props.projectId);
  isFetching.value = false;
  data.value = res || [];
};

const getDiffTypeColor = (diffType) => {
  switch (diffType) {
    case 'WORK_INCREASE':
      return 'green';
    case 'WORK_MOVED_TO_ADD_WORK':
      return 'blue';
    case 'WORK_DELETED':
    case 'MATERIAL_DELETED':
      return 'red';
    case 'WORK_DECREASE':
    case 'MATERIAL_DECREASE':
      return 'orange';
    case 'MATERIAL_NEW_ANOMALY':
    case 'MATERIAL_INCREASE_ANOMALY':
      return 'purple';
    case 'WORK_ZEROED':
      return 'grey';
    default:
      return 'grey';
  }
};

const getRowClass = (row) => {
  if (row.diff_type === 'WORK_INCREASE') return 'row-increase';
  if (row.diff_type === 'WORK_MOVED_TO_ADD_WORK') return 'row-moved';
  if (row.diff_type?.includes('DELETED')) return 'row-deleted';
  if (row.diff_type?.includes('ANOMALY')) return 'row-anomaly';
  return '';
};

const getDiffClass = (value) => {
  if (value > 0) return 'diff-positive';
  if (value < 0) return 'diff-negative';
  return '';
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return '-';
  return Number(value).toFixed(2);
};

const formatDiff = (value) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (num > 0) return '+' + num.toFixed(2);
  return num.toFixed(2);
};

watch(
  () => props.projectId,
  () => {
    loadData();
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.anomalies-container {
  padding: 0;
}

.description-block {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  border-radius: 8px;
  margin-bottom: 12px;
  border-left: 4px solid #1976d2;
}

.description-text {
  font-size: 14px;
  color: #424242;
  line-height: 1.5;
}

.description-subtitle {
  font-size: 12px;
  color: #666;
}

.cases-expansion {
  margin-bottom: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

:deep(.cases-header) {
  background: #fafafa;
  font-size: 13px;
}

.cases-content {
  padding: 16px;
  background: #fff;
}

.case-group {
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.case-title {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 10px;
  padding: 6px 10px;
  border-radius: 4px;
  
  &.positive {
    background: #e8f5e9;
    color: #2e7d32;
  }
  
  &.warning {
    background: #fff3e0;
    color: #e65100;
  }
  
  &.anomaly {
    background: #f3e5f5;
    color: #7b1fa2;
  }
}

.case-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #555;
  line-height: 1.5;
  
  .q-badge {
    flex-shrink: 0;
    font-size: 10px;
  }
}

.filters-section {
  margin-bottom: 16px;
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.filters-row-second {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.summary {
  display: flex;
  gap: 8px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-text {
  margin-top: 16px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.empty-subtext {
  margin-top: 8px;
  font-size: 14px;
  color: #666;
}

.diff-badge {
  font-size: 11px;
  padding: 4px 8px;
}

.work-name {
  font-weight: 500;
}

.reason-name {
  font-size: 12px;
  color: #555;
}

.diff-positive {
  color: #2e7d32;
  font-weight: 600;
}

.diff-negative {
  color: #c62828;
  font-weight: 600;
}

.row-increase {
  background-color: #e8f5e9 !important;
}

.row-moved {
  background-color: #e3f2fd !important;
}

.row-deleted {
  background-color: #ffebee !important;
}

.row-anomaly {
  background-color: #f3e5f5 !important;
}

</style>

